import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, onSnapshot, deleteDoc, deleteField, increment, runTransaction } from 'firebase/firestore';
import storage from './storageService';
import { getTodayDateKey, getStartOfWeek } from '../utils/dateHelpers';

/**
 * dbService.js
 * Centraliza a lÃ³gica de sincronizaÃ§Ã£o com o Firestore otimizada para o plano Spark (gratuito).
 * Armazena todos os dados do usuÃ¡rio em um Ãºnico documento comprimido para economizar leituras/escritas.
 */

const COLLECTION_NAME = 'user_data';
const DOCUMENT_ID = 'main';

/**
 * Remove recursivamente todas as chaves com valor 'undefined' de um objeto,
 * o que Ã© necessÃ¡rio para o Firestore aceitar o salvamento sem erros fatais.
 */
const sanitizePayload = (obj) => {
    if (obj === null || typeof obj !== 'object') return obj;

    // Para arrays, sanitiza cada elemento
    if (Array.isArray(obj)) {
        return obj.map(v => sanitizePayload(v));
    }

    const newObj = {};
    Object.keys(obj).forEach(key => {
        const value = obj[key];
        if (value !== undefined) {
            newObj[key] = sanitizePayload(value);
        }
    });
    return newObj;
};

/**
 * Salva todos os dados do usuÃ¡rio no Firestore com metadados de rastreamento.
 * OTIMIZADO: Evita sobrescrever campos sensÃ­veis se nÃ£o estiverem no payload.
 */
export const saveUserData = async (userId, rawData) => {
    if (!userId) {
        console.warn('DB_SERVICE: Tentativa de salvar sem userId');
        return;
    }

    try {
        // Sanitiza o payload para evitar erro: Function setDoc() called with invalid data. Unsupported field value: undefined
        const data = sanitizePayload(rawData);

        console.log(`DB_SERVICE: Sincronizando dados sanitizados para o usuÃ¡rio ${userId}...`);
        const userDocRef = doc(db, COLLECTION_NAME, userId, 'private', DOCUMENT_ID);

        // Busca o documento atual para preservar metadados e campos controlados pelo Admin
        const docSnap = await getDoc(userDocRef);
        let createdAt = serverTimestamp();
        let existingData = {};

        if (docSnap.exists()) {
            existingData = docSnap.data();
            if (existingData.createdAt) {
                createdAt = existingData.createdAt;
            }
        }

        // --- PROTEÃ‡ÃƒO CONTRA SOBRESCRITA (ADMIN E CONSISTÃŠNCIA) ---
        // Se o payload do cliente nÃ£o contÃ©m campos especÃ­ficos (como 'plan' ou 'isBlocked'),
        // mas eles existem no Firestore (provavelmente alterados pelo Admin), nÃ³s os preservamos.
        // O App.js agora deve ser mais seletivo no que envia.

        // SEGURANÃ‡A MÃ XIMA: ProteÃ§Ã£o contra apagamento em massa acidental (Ex: erro de sync inicial)
        if (existingData.transactions && existingData.transactions.length > 5 && (!data.transactions || data.transactions.length === 0) && !data.removeTransactionsField) {
            console.error('DB_SERVICE: BLOQUEIO DE SEGURANÃ‡A! Tentativa de salvar 0 transaÃ§Ãµes sobre um banco com dados. SincronizaÃ§Ã£o cancelada para evitar perda.');
            return false;
        }

        // Proteção para Metas/Conquistas e Configurações do Simulador
        // REGRA: um aparelho desatualizado (timestamp 0 ou mais antigo que a nuvem)
        // nunca pode sobrescrever a nuvem — nem com lista vazia, nem com lista antiga.
        // Toda edição legítima feita pelo app usa setGoalsWithTimestamp (Date.now()),
        // portanto sempre terá timestamp maior que o anterior.
        const existingGoalsUpdated = Number(existingData.goalsLastUpdated || 0);
        const incomingGoalsUpdated = Number(data.goalsLastUpdated || 0);
        const isLegitimateGoalsUpdate = incomingGoalsUpdated > 0 && incomingGoalsUpdated >= existingGoalsUpdated;
        const isGoalsIncomingStale = (incomingGoalsUpdated || 0) < existingGoalsUpdated ||
            ((incomingGoalsUpdated || 0) === 0 && (existingData.goals?.length || 0) > 0);

        if (existingData.goals && existingData.goals.length > 0 && (!data.goals || data.goals.length === 0) && !isLegitimateGoalsUpdate) {
            console.warn('DB_SERVICE: BLOQUEIO DE SEGURANÇA! Tentativa de salvar 0 metas sem timestamp atualizado. Preservando dados da nuvem.');
            delete data.goals;
            if (data.pinnedGoalId === null) delete data.pinnedGoalId;
            if (!data.simulationPeriod) delete data.simulationPeriod;
            if (data.simulationDailyAmount === 0) delete data.simulationDailyAmount;
        } else if (existingData.goals && existingData.goals.length > 0 && Array.isArray(data.goals) && data.goals.length > 0 && isGoalsIncomingStale) {
            console.warn('DB_SERVICE: BLOQUEIO DE SEGURANÇA! Tentativa de sobrescrever metas da nuvem com dados antigos (timestamp desatualizado). Preservando nuvem.');
            delete data.goals;
            delete data.goalsLastUpdated;
        }

        // Proteção para Veículos: UNIÃO POR ID (nunca last-write-wins puro).
        // Antes: um aparelho com timestamp mais novo (ex: editou odômetro) subia a
        // lista antiga e APAGAVA da nuvem o veículo criado em outro aparelho.
        // Agora: une nuvem + aparelho por String(id), respeitando tombstones de
        // exclusão. Itens com mesmo id: vence o lado com timestamp maior; listas
        // de manutenção/histórico são unidas por id para não perder registros
        // concorrentes.
        const mergeVehicleItem = (localItem, remoteItem, preferRemote) => {
            const base = preferRemote ? { ...localItem, ...remoteItem } : { ...remoteItem, ...localItem };
            const unionById = (a, b) => {
                const map = new Map();
                [...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])].forEach(it => {
                    if (it && it.id !== undefined && it.id !== null) map.set(String(it.id), it);
                });
                return [...map.values()];
            };
            if (localItem?.customMaintenance || remoteItem?.customMaintenance) {
                base.customMaintenance = unionById(localItem?.customMaintenance, remoteItem?.customMaintenance);
            }
            if (localItem?.maintenanceHistory || remoteItem?.maintenanceHistory) {
                const hist = unionById(localItem?.maintenanceHistory, remoteItem?.maintenanceHistory);
                hist.sort((x, y) => new Date(y.date || 0) - new Date(x.date || 0));
                base.maintenanceHistory = hist;
            }
            return base;
        };
        const mergeVehicleLists = (existingList, incomingList, preferIncoming) => {
            const map = new Map();
            (Array.isArray(existingList) ? existingList : []).forEach(v => { if (v && v.id !== undefined) map.set(String(v.id), v); });
            (Array.isArray(incomingList) ? incomingList : []).forEach(v => {
                if (!v || v.id === undefined) return;
                const key = String(v.id);
                if (map.has(key)) {
                    map.set(key, mergeVehicleItem(map.get(key), v, preferIncoming));
                } else {
                    map.set(key, v);
                }
            });
            return [...map.values()];
        };
        if (Array.isArray(data.vehicles) || Array.isArray(data.vehicleTombstones)) {
            const existingVehiclesTS = Number(existingData.vehiclesLastUpdated || 0);
            const incomingVehiclesTS = Number(data.vehiclesLastUpdated || 0);
            const existingList = Array.isArray(existingData.vehicles) ? existingData.vehicles : [];
            const incomingList = Array.isArray(data.vehicles) ? data.vehicles : [];
            // Tombstones: somente união, nunca sobrescrita (mesmo padrão dos atrasados).
            const existingVTomb = Array.isArray(existingData.vehicleTombstones) ? existingData.vehicleTombstones.map(String) : [];
            const incomingVTomb = Array.isArray(data.vehicleTombstones) ? data.vehicleTombstones.map(String) : [];
            const tombUnionV = [...existingVTomb];
            incomingVTomb.forEach(id => { if (!tombUnionV.includes(id)) tombUnionV.push(id); });
            const tombSetV = new Set(tombUnionV.slice(-1000));
            // Se o campo vehicles não veio no payload (save parcial), preserva a nuvem.
            if (!Array.isArray(data.vehicles)) {
                delete data.vehicles;
                delete data.vehiclesLastUpdated;
            } else {
                const preferIncoming = incomingVehiclesTS >= existingVehiclesTS;
                const merged = mergeVehicleLists(existingList, incomingList, preferIncoming)
                    .filter(v => v && !tombSetV.has(String(v.id)));
                data.vehicles = merged;
                data.vehiclesLastUpdated = Math.max(existingVehiclesTS, incomingVehiclesTS);
                // Se o merge resultou em lista vazia mas nenhum tombstone cobre a frota
                // existente, é upload acidental de aparelho limpo: preserva a nuvem.
                if (merged.length === 0 && existingList.length > 0 && tombSetV.size === 0) {
                    console.warn('DB_SERVICE: BLOQUEIO DE SEGURANÇA! Tentativa de salvar frota vazia sem tombstones. Preservando nuvem.');
                    delete data.vehicles;
                    delete data.vehiclesLastUpdated;
                }
            }
            if (tombUnionV.length > 0) {
                data.vehicleTombstones = tombUnionV.slice(-1000);
            } else if (data.vehicleTombstones) {
                delete data.vehicleTombstones;
            }
        }

        // Proteção para Despesas Fixas: nunca sobrescreve lista nova da nuvem com lista
        // vazia/antiga de um aparelho desatualizado (ex: PC aberto há dias salva por cima
        // da edição feita no celular). Exige timestamp mais novo; timestamp 0 com nuvem
        // populada é sempre tratado como desatualizado.
        // MOTIVO DA RESSURREIÇÃO: sem essa trava, o aparelho antigo confirmava o upload
        // com a lista antiga e as contas já pagas/excluídas voltavam para todos.
        const existingFixedTS = Number(existingData.fixedExpensesLastUpdated || 0);
        const incomingFixedTS = Number(data.fixedExpensesLastUpdated || 0);
        const existingFixedLen = Array.isArray(existingData.fixedExpenses) ? existingData.fixedExpenses.length : 0;
        const incomingFixedLen = Array.isArray(data.fixedExpenses) ? data.fixedExpenses.length : 0;
        const isFixedIncomingStale = incomingFixedTS < existingFixedTS ||
            (incomingFixedTS === 0 && existingFixedLen > 0);
        if (existingFixedLen > 0 && (incomingFixedLen === 0 || isFixedIncomingStale)) {
            console.warn('DB_SERVICE: BLOQUEIO DE SEGURANÇA! Tentativa de sobrescrever despesas fixas da nuvem com dados vazios/antigos. Preservando nuvem.');
            delete data.fixedExpenses;
            delete data.fixedExpensesLastUpdated;
        }

        // Proteção para Atrasados: mesma lógica (baixa em lote zera o array legitimamente,
        // mas só quando o timestamp acompanha a mudança). Sem timestamp novo, uma baixa
        // feita em outro aparelho seria desfeita pelo upload do aparelho desatualizado —
        // é assim que as despesas pagas "voltavam".
        const existingPastDueTS = Number(existingData.pastDueExpensesLastUpdated || 0);
        const incomingPastDueTS = Number(data.pastDueExpensesLastUpdated || 0);
        const existingPastDueLen = Array.isArray(existingData.pastDueExpenses) ? existingData.pastDueExpenses.length : 0;
        const incomingPastDueLen = Array.isArray(data.pastDueExpenses) ? data.pastDueExpenses.length : 0;
        const isPastDueIncomingStale = incomingPastDueTS < existingPastDueTS ||
            (incomingPastDueTS === 0 && existingPastDueLen > 0);
        if (existingPastDueLen > 0 && (incomingPastDueLen === 0 || isPastDueIncomingStale)) {
            console.warn('DB_SERVICE: BLOQUEIO DE SEGURANÇA! Tentativa de sobrescrever atrasados da nuvem com dados vazios/antigos. Preservando nuvem.');
            delete data.pastDueExpenses;
            delete data.pastDueExpensesLastUpdated;
        }

        // Tombstones de atrasados: SOMENTE união, nunca sobrescrita.
        // Cada aparelho só adiciona IDs; se um aparelho desatualizado enviar uma lista
        // menor/antiga, ela é mesclada com a da nuvem em vez de apagá-la. Sem isso, a
        // proteção de uma parcela já paga seria perdida e a dívida voltaria.
        const existingTombstones = Array.isArray(existingData.pastDueTombstones) ? existingData.pastDueTombstones : [];
        const incomingTombstones = Array.isArray(data.pastDueTombstones) ? data.pastDueTombstones : [];
        const tombUnion = [...existingTombstones];
        incomingTombstones.forEach(id => { if (typeof id === 'string' && !tombUnion.includes(id)) tombUnion.push(id); });
        if (tombUnion.length > 0) {
            data.pastDueTombstones = tombUnion.slice(-1000);
        } else if (data.pastDueTombstones) {
            delete data.pastDueTombstones;
        }

        // --- MIGRACAO PARA SUBCOLECOES ---
        const shouldDeleteTransactionsField = data.removeTransactionsField === true;

        // Sempre garantimos que o array de transaÃ§Ãµes nÃ£o seja salvo no documento 'main'
        if (data.transactions) {
            delete data.transactions;
        }
        if (data.removeTransactionsField !== undefined) {
            delete data.removeTransactionsField;
        }

        const dataToSave = {
            ...data,
            createdAt: createdAt,
            lastSynced: serverTimestamp(),
            isLoggedOut: false,
            loggedOutAt: null
        };

        // Se o payload contiver 'userProfile', fazemos o merge com o existente para nÃ£o perder campos do Admin
        if (data.userProfile && existingData.userProfile) {
            dataToSave.userProfile = {
                ...existingData.userProfile,
                ...data.userProfile
            };
        }

        if (shouldDeleteTransactionsField) {
            dataToSave.transactions = deleteField();
        }

        await setDoc(userDocRef, dataToSave, { merge: true });
        console.log('DB_SERVICE: Sucesso! Dados salvos no Firestore.');
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro crÃ­tico ao salvar no Firestore:', error);
        return false;
    }
};

/**
 * FunÃ§Ãµes auxiliares para cÃ¡lculo de comissÃµes e perÃ­odos (Sincronizadas com o Admin)
 */
const parseAnyDate = (dateVal) => {
    if (!dateVal) return null;
    if (dateVal instanceof Date) return dateVal;
    if (typeof dateVal.toDate === 'function') return dateVal.toDate();
    if (typeof dateVal === 'number') return new Date(dateVal);
    if (typeof dateVal === 'object' && 'seconds' in dateVal) return new Date(dateVal.seconds * 1000);
    const parsed = new Date(dateVal);
    return isNaN(parsed.getTime()) ? null : parsed;
};

const getPeriodId = (dateVal) => {
    const d = parseAnyDate(dateVal) || new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 4 - (d.getDay() || 7));
    const yearStart = new Date(d.getFullYear(), 0, 1);
    const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    return `${d.getFullYear()}-W${String(weekNo).padStart(2, '0')}`;
};

const getPartnerPayouts = async (partnerId) => {
    try {
        const q = query(collection(db, 'partner_payouts'), where('partnerId', '==', partnerId));
        const snap = await getDocs(q);
        const payouts = {};
        snap.forEach(doc => { payouts[doc.data().periodId] = doc.data(); });
        return payouts;
    } catch (e) {
        console.error('DB_SERVICE: Erro ao buscar pagamentos:', e);
        return {};
    }
};

/**
 * Atualiza apenas um campo especÃ­fico ou objeto aninhado usando dot-notation.
 * Ãštil para evitar sobrescritas de objetos inteiros.
 */
export const updateUserDataField = async (userId, path, value) => {
    if (!userId) return false;
    try {
        const userDocRef = doc(db, COLLECTION_NAME, userId, 'private', DOCUMENT_ID);
        await updateDoc(userDocRef, {
            [path]: value,
            lastSynced: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error(`DB_SERVICE: Erro ao atualizar campo ${path}:`, error);
        return false;
    }
};

/**
 * NOVA ESTRUTURA (SUBCOLEÃ‡ÃƒO): Salva uma Ãºnica transaÃ§Ã£o
 */
/**
 * Finaliza um comando remoto com segurança.
 * Usa update() com dot-notation (set() trata os pontos como nome literal do campo)
 * e transaction para não sobrescrever um comando mais novo enviado pelo admin.
 */
export const completeRemoteCommand = async (userId, commandId, status = 'completed', errorMessage = null) => {
    if (!userId || !commandId) return false;

    try {
        const userDocRef = doc(db, COLLECTION_NAME, userId, 'private', DOCUMENT_ID);

        await runTransaction(db, async (transaction) => {
            const snap = await transaction.get(userDocRef);
            if (!snap.exists()) return;

            const currentCommand = snap.data()?.remoteCommand;
            if (!currentCommand || String(currentCommand.id) !== String(commandId)) return;

            const payload = {
                'remoteCommand.status': status,
                'remoteCommand.completedAt': new Date().toISOString()
            };

            if (errorMessage) {
                payload['remoteCommand.error'] = String(errorMessage).slice(0, 500);
            }

            transaction.update(userDocRef, payload);
        });

        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao finalizar comando remoto:', error);
        return false;
    }
};

export const saveTransactionRemote = async (userId, transaction) => {
    if (!userId || !transaction || !transaction.id) return false;
    try {
        const sanitized = sanitizePayload(transaction);
        const txRef = doc(db, COLLECTION_NAME, String(userId), 'transactions', String(transaction.id));
        await setDoc(txRef, {
            ...sanitized,
            updatedAt: serverTimestamp()
        }, { merge: true });
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao salvar transaÃ§Ã£o remota:', error);
        return false;
    }
};

/**
 * NOVA ESTRUTURA (SUBCOLEÃ‡ÃƒO): Exclui uma transaÃ§Ã£o
 */
export const deleteTransactionRemote = async (userId, transactionId) => {
    if (!userId || !transactionId) return false;
    try {
        const txRef = doc(db, COLLECTION_NAME, String(userId), 'transactions', String(transactionId));
        await deleteDoc(txRef);
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao excluir transaÃ§Ã£o remota:', error);
        return false;
    }
};

/**
 * NOVA ESTRUTURA (SUBCOLEÃ‡ÃƒO): Busca todas as transaÃ§Ãµes de um usuÃ¡rio
 */
export const fetchTransactionsRemote = async (userId) => {
    if (!userId) return [];
    try {
        const txCollectionRef = collection(db, COLLECTION_NAME, String(userId), 'transactions');
        const snap = await getDocs(txCollectionRef);
        const txs = [];
        snap.forEach(document => txs.push(document.data()));
        return txs;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao buscar transaÃ§Ãµes remotas:', error);
        return [];
    }
};

/**
 * NOVA ESTRUTURA (SUBCOLEÃ‡ÃƒO): Busca apenas transaÃ§Ãµes atualizadas apÃ³s um timestamp
 * Otimizado para sincronizaÃ§Ã£o incremental em tempo real (Spark Plan Friendly)
 */
export const fetchUpdatedTransactionsRemote = async (userId, sinceTimestamp) => {
    if (!userId) return [];
    try {
        const txCollectionRef = collection(db, COLLECTION_NAME, String(userId), 'transactions');

        // Se sinceTimestamp for 0, busca tudo (fallback)
        if (!sinceTimestamp) return fetchTransactionsRemote(userId);

        // Converte timestamp numÃ©rico para Date se necessÃ¡rio
        const dateLimit = new Date(sinceTimestamp);

        const q = query(
            txCollectionRef,
            where('updatedAt', '>', dateLimit)
        );

        const snap = await getDocs(q);
        const txs = [];
        snap.forEach(document => txs.push(document.data()));

        if (txs.length > 0) {
            console.log(`DB_SERVICE: Detectadas ${txs.length} transaÃ§Ãµes atualizadas desde ${dateLimit.toISOString()}`);
        }

        return txs;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao buscar transaÃ§Ãµes incrementais:', error);
        return [];
    }
};

/**
 * Atualiza mÃ©tricas de resumo no documento principal para facilitar leitura pelo Admin.
 */
export const updateUserMetricsRemote = async (userId, metrics) => {
    if (!userId || !metrics) return false;
    try {
        const userDocRef = doc(db, COLLECTION_NAME, String(userId), 'private', DOCUMENT_ID);
        await updateDoc(userDocRef, {
            transactionsCount: metrics.transactionsCount ?? 0,
            totalIncome: metrics.totalIncome ?? 0,
            totalExpense: metrics.totalExpense ?? 0,
            vehiclesCount: metrics.vehiclesCount ?? 0,
            lastSynced: serverTimestamp()
        });
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao atualizar mÃ©tricas resumidas:', error);
        return false;
    }
};

/**
 * Exclui permanentemente todos os dados do usuÃ¡rio e a referÃªncia privada.
 */
export const deleteUserAccount = async (userId) => {
    if (!userId) return false;
    try {
        const { deleteDoc, getDocs, collection: fbCollection } = await import('firebase/firestore');

        // 1a. Apaga todas as transações da subcoleção (evita dados órfãos no Firestore)
        try {
            const txCollRef = fbCollection(db, COLLECTION_NAME, userId, 'transactions');
            const txSnap = await getDocs(txCollRef);
            await Promise.all(txSnap.docs.map(d => deleteDoc(d.ref)));
            console.log(`DB_SERVICE: ${txSnap.size} transações da subcoleção deletadas.`);
        } catch (txErr) {
            console.warn('DB_SERVICE: Erro ao limpar subcoleção de transações:', txErr);
        }


        console.log(`DB_SERVICE: Iniciando exclusÃ£o definitiva de dados para ${userId}...`);

        // 1. Deletar documento privado principal
        const userDocRef = doc(db, COLLECTION_NAME, userId, 'private', DOCUMENT_ID);
        await deleteDoc(userDocRef);

        // 2. Opcional: Se houver outras subcoleÃ§Ãµes em /user_data/{userId}/..., deletar aqui
        // No esquema atual, tudo estÃ¡ no documento 'main'.

        console.log('DB_SERVICE: Dados do Firestore excluÃ­dos com sucesso.');
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao excluir dados do Firestore:', error);
        return false;
    }
};

/**
 * Limpa todos os dados do usuario SEM excluir a conta Firebase Auth.
 * Seguro: mantem UID, autenticacao e campos de assinatura/admin intactos.
 */
export const clearUserDataRemote = async (userId) => {
    if (!userId) return false;
    try {
        const { deleteDoc: fbDeleteDoc, getDocs, collection: fbCollection, setDoc: fbSetDoc } = await import('firebase/firestore');
        // 1. Apaga subcoleção de transações
        try {
            const txCollRef = fbCollection(db, COLLECTION_NAME, userId, 'transactions');
            const txSnap = await getDocs(txCollRef);
            await Promise.all(txSnap.docs.map(d => fbDeleteDoc(d.ref)));
            console.log('DB_SERVICE: ' + txSnap.size + ' transacoes limpas.');
        } catch (txErr) {
            console.warn('DB_SERVICE: Erro ao limpar subcoleção:', txErr);
        }
        // 2. Zera documento principal - merge:true preserva subscription/role do admin
        const userDocRef = doc(db, COLLECTION_NAME, userId, 'private', DOCUMENT_ID);
        await fbSetDoc(userDocRef, {
            transactions: [], transactionsCount: 0, kmRecords: {}, dailyManualStats: {},
            vehicles: [], fixedExpenses: [], pastDueExpenses: [], pastDueTombstones: [],
            scheduledTrips: [], goals: [], customCategories: { income: [], expense: [] },
            dailyGoal: 0, manualVarCost: 0, manualSavedAmount: 0,
            dataCleared: true, dataClearedAt: serverTimestamp(), lastSynced: serverTimestamp(),
        }, { merge: true });
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao limpar dados:', error);
        return false;
    }
};

/**
 * Marca o usuÃ¡rio como deslogado no banco de dados.
 */
export const markUserAsLoggedOut = async (userId) => {
    if (!userId) return;
    try {
        const userDocRef = doc(db, COLLECTION_NAME, userId, 'private', DOCUMENT_ID);
        await setDoc(userDocRef, {
            isLoggedOut: true,
            loggedOutAt: serverTimestamp()
        }, { merge: true });
        console.log(`DB_SERVICE: UsuÃ¡rio ${userId} marcado como deslogado.`);
    } catch (error) {
        console.error('DB_SERVICE: Erro ao marcar logout:', error);
    }
};

/**
 * Busca os dados do usuÃ¡rio no Firestore.
 */
export const fetchUserData = async (userId) => {
    if (!userId) return null;

    try {
        const userDocRef = doc(db, COLLECTION_NAME, userId, 'private', DOCUMENT_ID);
        const docSnap = await getDoc(userDocRef);

        if (docSnap.exists()) {
            return docSnap.data();
        } else {
            console.log('Nenhum dado encontrado no Firestore para este usuÃ¡rio.');
            return null;
        }
    } catch (error) {
        console.error('Erro ao buscar dados no Firestore:', error);
        throw error; // Propaga o erro para que o App.js saiba que falhou
    }
};

/**
 * Busca estatÃ­sticas detalhadas da nuvem para diagnÃ³stico.
 */
export const fetchCloudStats = async (userId) => {
    if (!userId) return null;
    try {
        // 1. Busca documento principal
        const mainData = await fetchUserData(userId);
        if (!mainData) return null;

        // 2. Busca contagem real na subcoleÃ§Ã£o de transaÃ§Ãµes (Com tratamento de erro de permissÃ£o)
        let transactionsCount = 0;
        try {
            const txCollectionRef = collection(db, COLLECTION_NAME, userId, 'transactions');
            const txSnap = await getDocs(txCollectionRef);
            transactionsCount = txSnap.size;
        } catch (txError) {
            console.warn('DB_SERVICE: NÃ£o foi possÃ­vel ler subcoleÃ§Ã£o de transaÃ§Ãµes (ProvÃ¡vel erro de permissÃ£o):', txError);
            // Fallback para o contador do documento principal se falhar
            transactionsCount = mainData.transactionsCount || (mainData.transactions?.length || 0);
        }

        return {
            ...mainData,
            transactionsCount: transactionsCount,
            legacyTransactionsCount: mainData.transactions?.length || 0,
            isMigrated: !mainData.transactions || mainData.transactions.length === 0
        };
    } catch (error) {
        console.error('DB_SERVICE: Erro ao buscar estatÃ­sticas da nuvem:', error);
        throw error;
    }
};


/**
 * Resgata um cupom de parceria ou global (Validado pelo Servidor)
 */
export const redeemCoupon = async (userId, couponCode) => {
    try {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) throw new Error('UsuÃ¡rio nÃ£o autenticado.');

        const idToken = await user.getIdToken();
        const FUNCTION_URL = 'https://southamerica-east1-meupainel-21dba.cloudfunctions.net/redeemCoupon';

        const response = await fetch(FUNCTION_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ couponCode: couponCode }),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || `Erro ao validar cupom (HTTP ${response.status})`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao resgatar cupom:', error);
        throw error;
    }
};

/**
 * Busca os indicados do parceiro logado (User-Facing).
 * Retorna lista simplificada e contadores.
 */
export const getMyReferrals = async (userId) => {
    if (!userId) {
        console.warn('DB_SERVICE: getMyReferrals chamado sem userId');
        return { totalReferrals: 0, trials: 0, conversions: 0, referrals: [], wallet: null };
    }
    try {
        // ImportaÃ§Ãµes dinÃ¢micas para manter performance
        const { collectionGroup, query, where, getDocs, doc, getDoc } = await import('firebase/firestore');
        const { calculateCommission } = await import('../config/subscriptionPlans'); // Importa config centralizada

        // 0. Verifica configuraÃ§Ãµes do prÃ³prio Parceiro (se pode ver comissÃµes)
        const partnerRef = doc(db, 'user_data', userId, 'private', 'main');
        const partnerSnap = await getDoc(partnerRef);
        const partnerData = partnerSnap.exists() ? partnerSnap.data() : {};

        // Flag de visibilidade (PadrÃ£o: FALSE se nÃ£o existir)
        const showCommission = partnerData.showCommission === true;
        const customCommissionRate = partnerData.customCommissionRate !== undefined ? partnerData.customCommissionRate : null;

        // Pega desconto e dias extras do cupom do parceiro
        let myDiscount = 10;
        let myExtraDays = 0;
        if (partnerData.partnerCoupon) {
            try {
                const couponRef = doc(db, 'coupons', partnerData.partnerCoupon);
                const couponSnap = await getDoc(couponRef);
                if (couponSnap.exists()) {
                    const couponData = couponSnap.data();
                    myDiscount = couponData.discountPercentage ?? 10;
                    myExtraDays = couponData.extraDays || 0;
                }
            } catch (e) { console.log('Erro ao buscar desconto:', e); }
        }


        // Busca pagamentos jÃ¡ realizados
        const payoutsData = await getPartnerPayouts(userId);

        // Busca usuÃ¡rios que tÃªm 'referredBy' igual ao meu ID
        const q = query(collectionGroup(db, 'private'), where('referredBy', '==', userId));
        const snapshot = await getDocs(q);

        const referrals = [];
        let totalTrials = 0;
        let totalConversions = 0;
        let totalAwaiting = 0;
        let totalEngaged = 0;

        // Carteira (SÃ³ calcula se showCommission for true)
        let wallet = showCommission ? {
            totalEarned: 0,    // O que jÃ¡ ganhou (Pagos)
            projected: 0,      // O que pode ganhar (Testes + Pagos)
            available: 0,      // O que pode sacar (SimulaÃ§Ã£o simples por enquanto)
            totalRefunds: 0,
            totalRefundValue: 0,
            totalCancellations: 0,
            totalCancelValue: 0
        } : null;

        const now = new Date();

        snapshot.forEach(doc => {
            const data = doc.data();
            // --- DETECÃ‡ÃƒO DE STATUS E FINANCEIRO ---
            const sub = data.subscription || {};

            // DetecÃ§Ã£o de Reembolso/Cancelamento (Sincronizado com Admin)
            const cancelReason = sub.cancelReason || '';
            const isRefund = ['refunded', 'REVOKED', 'charged_back'].includes(cancelReason) || data.status === 'refunded' || sub.status === 'refunded';
            const isCancel = (data.status === 'cancelled' || sub.status === 'cancelled' || cancelReason) && !isRefund;

            // Considera pago APENAS se estiver ativo, nÃ£o for trial e nÃ£o for reembolso/cancelamento
            const isExplicitlyPaid = (sub.plan === 'premium' || sub.plan === 'vip') && sub.isTrial !== true;
            const isPaid = isExplicitlyPaid && !isRefund && !isCancel;

            const planId = sub.planId || (isPaid ? (sub.plan === 'vip' ? 'vip' : 'premium_mensal') : 'free');
            const trialExpiresAt = sub.expiresAt ? (sub.expiresAt.toDate ? sub.expiresAt.toDate() : new Date(sub.expiresAt)) : null;
            const referralDate = data.referralDate ? data.referralDate.toDate() : (data.createdAt ? data.createdAt.toDate() : now);

            let status = 'TRIAL';
            let daysLeft = 0;

            if (isPaid) {
                status = 'PAID';
                totalConversions++;
            } else if (isRefund || isCancel) {
                status = isRefund ? 'REFUNDED' : 'CANCELLED';
            } else {
                if (trialExpiresAt && trialExpiresAt > now) {
                    status = 'TRIAL';
                    const diffTime = Math.abs(trialExpiresAt - now);
                    daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                    totalTrials++;
                } else {
                    const gracePeriodDays = 10;
                    const referenceDate = trialExpiresAt || referralDate;
                    const maxAwaitingDate = new Date(referenceDate.getTime() + (gracePeriodDays * 24 * 60 * 60 * 1000));

                    if (now > maxAwaitingDate) {
                        status = 'EXPIRED_REFERRAL';
                    } else {
                        status = 'AWAITING_PURCHASE';
                        totalAwaiting++;
                    }
                }
            }

            // --- CÃLCULO FINANCEIRO REAL (Sincronizado com Admin) ---
            let commissionValue = 0;
            let isCommissionPaid = false;
            let subscriptionDate = null;
            let commissionPeriodId = null;

            if (showCommission) {
                let refCommission = Number(sub.commissionSnapshot?.value) || 0;

                if (isPaid || isRefund || isCancel) {
                    if (isRefund || isCancel) {
                        if (isRefund) {
                            wallet.totalRefunds++;
                        } else {
                            wallet.totalCancellations++;
                        }
                        // Se nÃ£o tem snapshot, calcula para negativar
                        if (!refCommission) {
                            refCommission = calculateCommission(planId, myDiscount, customCommissionRate);
                        }
                        commissionValue = -Math.abs(refCommission);
                    } else {
                        // Fallback: se o snapshot estiver ausente, calcula
                        if (!refCommission) {
                            refCommission = calculateCommission(planId, myDiscount, customCommissionRate);
                        }
                        commissionValue = refCommission;
                    }

                    // SALDO DISPONÃVEL ( commissionReal ): SÃ“ ADICIONA SE NÃƒO FOI PAGO AINDA
                    const paymentDateStr = sub.commissionSnapshot?.date || sub.updatedAt || data.updatedAt;
                    const periodId = getPeriodId(paymentDateStr || referralDate);

                    if (payoutsData[periodId]) {
                        isCommissionPaid = true;
                    } else {
                        wallet.totalEarned += commissionValue;
                        wallet.available += commissionValue;

                        // SÃ³ adicionamos ao contador visual de estornos/cancelamentos se for pendente (nÃ£o pago)
                        if (isRefund) wallet.totalRefundValue += commissionValue;
                        if (isCancel) wallet.totalCancelValue += commissionValue;
                    }

                    subscriptionDate = parseAnyDate(paymentDateStr) || referralDate;
                    commissionPeriodId = periodId;
                }
                // ProjeÃ§Ã£o (Testando ou Aguardando)
                else if (status === 'TRIAL' || status === 'AWAITING_PURCHASE') {
                    const projectedValue = calculateCommission('premium_mensal', myDiscount, customCommissionRate);
                    wallet.projected += projectedValue;
                    commissionValue = projectedValue;
                }
            }

            let lastSyncedStr = data.lastSynced || null;
            if (lastSyncedStr) {
                // Se for um Timestamp do Firestore, usa toDate()
                const lastSyncDate = lastSyncedStr.toDate ? lastSyncedStr.toDate() : new Date(lastSyncedStr);
                lastSyncedStr = lastSyncDate.toISOString(); // Padroniza para envio ao componente
                if ((now - lastSyncDate) < 7 * 24 * 60 * 60 * 1000) {
                    totalEngaged++;
                }
            }

            referrals.push({
                id: doc.ref.path.split('/')[1],
                name: data.userProfile?.name || 'UsuÃ¡rio',
                status: (isRefund || isCancel) ? (isRefund ? 'REFUNDED' : 'CANCELLED') : status,
                realStatus: status, // Status tÃ©cnico
                daysLeft: daysLeft,
                plan: sub.plan === 'vip' ? 'VIP' : (sub.planId ? sub.planId.replace('premium_', '').toUpperCase() : 'FREE'),
                date: referralDate,
                subscriptionDate: subscriptionDate, // Data real da assinatura ou fallback
                isCommissionPaid: isCommissionPaid, // Indica se a comissÃ£o desta conversÃ£o jÃ¡ foi paga ao parceiro
                commissionPeriodId: commissionPeriodId, // Semana de repasse (ex: 2026-W09)
                commission: showCommission ? commissionValue : null,
                lastSynced: lastSyncedStr,
                appVersion: data.appVersion || null
            });
        });

        // Ordena por data de assinatura (se existir) ou por data (mais recente)
        referrals.sort((a, b) => {
            const dateA = a.subscriptionDate?.getTime() || a.date?.getTime() || 0;
            const dateB = b.subscriptionDate?.getTime() || b.date?.getTime() || 0;
            return dateB - dateA;
        });

        // Taxa de comissÃ£o: usa a customCommissionRate se definida pelo admin, senÃ£o o padrÃ£o (50%)
        const commissionRate = customCommissionRate !== null ? customCommissionRate : 50;

        return {
            totalReferrals: referrals.length,
            trials: totalTrials,
            conversions: totalConversions,
            awaiting: totalAwaiting,
            totalEngaged: totalEngaged,
            referrals,
            wallet,
            showCommission,
            couponDiscount: myDiscount,
            couponExtraDays: myExtraDays,
            commissionRate // Taxa % que o parceiro recebe (configurada pelo admin)
        };

    } catch (error) {
        console.error('DB_SERVICE: Erro detalhado ao buscar meus indicados:', error);
        if (error.code === 'failed-precondition') {
            console.error('CONDIÃ‡ÃƒO_FALHOU: Provavelmente falta um Ã­ndice para query de CollectionGroup.');
        }
        return { totalReferrals: 0, trials: 0, conversions: 0, referrals: [], wallet: null };
    }
};
/**
 * Atualiza a versÃ£o do app do usuÃ¡rio no Firestore.
 */
export const syncAppVersion = async (userId, version) => {
    if (!userId || !version) return;
    try {
        const userDocRef = doc(db, COLLECTION_NAME, userId, 'private', DOCUMENT_ID);
        await setDoc(userDocRef, {
            appVersion: version
        }, { merge: true });
        console.log(`DB_SERVICE: VersÃ£o do app (${version}) sincronizada para o usuÃ¡rio ${userId}.`);
    } catch (error) {
        console.error('DB_SERVICE: Erro ao sincronizar versÃ£o do app:', error);
    }
};

/**
 * Envia uma mensagem de suporte para o administrador com proteÃ§Ã£o contra spam.
 */
export const sendSupportMessage = async (userId, userProfile, messageData) => {
    if (!userId) return false;

    // ProteÃ§Ã£o contra spam: Intervalo de 2 minutos (120000ms)
    // ProteÃ§Ã£o contra spam configurÃ¡vel
    const LAST_SEND_KEY = `last_support_send_${userId}`;
    const storage = require('./storageService').default;
    const lastSend = storage.getItemSync(LAST_SEND_KEY, 0);
    const now = Date.now();

    try {
        const {
            collection, doc, getDoc, updateDoc, setDoc, query, where,
            orderBy, limit, onSnapshot, getDocs, writeBatch, arrayUnion,
            arrayRemove, serverTimestamp, increment, runTransaction, addDoc
        } = await import('firebase/firestore');

        // 1. Busca configuraÃ§Ãµes globais de cooldown
        const settingsRef = doc(db, 'settings', 'support');
        const settingsSnap = await getDoc(settingsRef);
        const settings = settingsSnap.exists() ? settingsSnap.data() : { cooldownEnabled: true, cooldownMinutes: 2 };

        if (settings.cooldownEnabled) {
            const cooldownMs = (settings.cooldownMinutes || 2) * 60 * 1000;
            if (now - lastSend < cooldownMs) {
                const remainingSecs = Math.ceil((cooldownMs - (now - lastSend)) / 1000);
                const remainingMins = Math.ceil(remainingSecs / 60);
                throw new Error(`Aguarde ${remainingMins} ${remainingMins === 1 ? 'minuto' : 'minutos'} para enviar outra mensagem.`);
            }
        }

        // 2. Verifica se jÃ¡ existe um ticket aberto (status != 'resolved')
        const q = query(
            collection(db, 'support_messages'),
            where('userId', '==', userId),
            where('status', 'in', ['new', 'pending']),
            limit(1)
        );
        const openTicketSnap = await getDocs(q);

        if (!openTicketSnap.empty) {
            // Se existir, adiciona como resposta ao ticket atual (Chat ContÃ­nuo)
            const ticketDoc = openTicketSnap.docs[0];
            const ticketRef = doc(db, 'support_messages', ticketDoc.id);

            await updateDoc(ticketRef, {
                replies: arrayUnion({
                    text: messageData.message.substring(0, 1000),
                    role: 'user',
                    sentAt: new Date().toISOString()
                }),
                status: 'new', // Volta para 'novo' para alertar o admin
                userRead: true,
                vehicleInfo: messageData.vehicleInfo || 'N/A', // Atualiza o veÃ­culo atual na conversa
                updatedAt: serverTimestamp()
            });

            storage.setItem(LAST_SEND_KEY, now);
            return true;
        }

        // 3. Se nÃ£o houver ticket aberto, cria um novo
        const messagesRef = collection(db, 'support_messages');

        await addDoc(messagesRef, {
            userId,
            userName: userProfile?.name || 'UsuÃ¡rio',
            userEmail: userProfile?.email || userProfile?.phone || 'Sem e-mail/telefone',
            vehicleInfo: messageData.vehicleInfo || 'N/A', // Novo campo
            type: messageData.type, // 'bug' | 'suggestion' | 'other'
            subject: messageData.subject.substring(0, 100), // SanitizaÃ§Ã£o bÃ¡sica de tamanho
            message: messageData.message.substring(0, 1000),
            status: 'new', // 'new' | 'pending' | 'resolved'
            userRead: true, // UsuÃ¡rio jÃ¡ leu a que ele mesmo mandou
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        // Atualiza timestamp do Ãºltimo envio
        storage.setItem(LAST_SEND_KEY, now);

        console.log('DB_SERVICE: Mensagem de suporte enviada com sucesso.');
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao enviar mensagem de suporte:', error);
        throw error;
    }
};

/**
 * Ouvinte para mensagens nÃ£o lidas do usuÃ¡rio (respostas do admin).
 */
export const subscribeUnreadSupportMessages = (userId, onUpdate) => {
    if (!userId) return () => { };
    try {
        const q = query(
            collection(db, 'support_messages'),
            where('userId', '==', userId),
            where('userRead', '==', false)
        );

        return onSnapshot(q, (snapshot) => {
            onUpdate(snapshot.size);
        });
    } catch (error) {
        console.error('DB_SERVICE: Erro ao assinar mensagens nÃ£o lidas:', error);
        return () => { };
    }
};

/**
 * Ouve o documento de feature flags (settings/features).
 * PadrÃ£o seguro: se o documento nÃ£o existir, todas as features ficam ocultas.
 */
export const subscribeFeatureFlags = (onUpdate) => {
    try {
        const flagsRef = doc(db, 'settings', 'features');
        return onSnapshot(flagsRef, (docSnap) => {
            if (docSnap.exists()) {
                onUpdate(docSnap.data());
            } else {
                // PadrÃ£o seguro: tudo oculto atÃ© o Admin configurar
                onUpdate({ jornadaPro: { visible: false } });
            }
        });
    } catch (error) {
        console.error('DB_SERVICE: Erro ao assinar feature flags:', error);
        return () => { };
    }
};

/**
 * Ouve o documento de configuraÃ§Ãµes do banner global.
 */
export const subscribeGlobalBanner = (onUpdate) => {
    try {
        const bannerRef = doc(db, 'settings', 'banner');
        return onSnapshot(bannerRef, (docSnap) => {
            if (docSnap.exists()) {
                onUpdate(docSnap.data());
            } else {
                onUpdate({ active: false });
            }
        });
    } catch (error) {
        console.error('DB_SERVICE: Erro ao assinar banner global:', error);
        return () => { };
    }
};

/**
 * Ouve o documento de configurações gerais do app (settings/app).
 * Gerencia os dias de teste padrão (defaultTrialDays) configurados no painel admin.
 */
export const subscribeAppSettings = (onUpdate) => {
    try {
        const appRef = doc(db, 'settings', 'app');
        return onSnapshot(appRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (data.defaultTrialDays !== undefined) {
                    storage.setItem('defaultTrialDays', Number(data.defaultTrialDays) || 7);
                }
                onUpdate(data);
            } else {
                onUpdate({ defaultTrialDays: 7 });
            }
        }, (error) => {
            console.warn('DB_SERVICE: Falha ao assinar settings/app:', error);
            const cached = storage.getItemSync('defaultTrialDays', 7);
            onUpdate({ defaultTrialDays: Number(cached) || 7 });
        });
    } catch (error) {
        console.error('DB_SERVICE: Erro ao assinar app settings:', error);
        return () => { };
    }
};

/**
 * Retorna os dias de teste padrão em cache ou 7 como fallback seguro.
 */
export const getDefaultTrialDays = () => {
    return Number(storage.getItemSync('defaultTrialDays', 7)) || 7;
};

/**
 * Ouve as configurações de pop-ups de alerta de assinatura e modo leitura (settings/subscription_alerts).
 */
export const subscribeSubscriptionAlerts = (onUpdate) => {
    try {
        const alertsRef = doc(db, 'settings', 'subscription_alerts');
        return onSnapshot(alertsRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                storage.setItem('subscriptionAlertSettings', data);
                onUpdate(data);
            } else {
                const defaults = {
                    enabled: true,
                    trialWarningDays: 3,
                    planWarningDays: 3,
                    trialTitle: 'Seu período de teste está terminando!',
                    trialMessage: 'Faltam {dias} dias para o término do seu teste gratuito. Assine o plano Premium para continuar aproveitando todos os recursos sem nenhuma pausa.',
                    planTitle: 'Sua assinatura Premium está vencendo',
                    planMessage: 'Faltam {dias} dias para a renovação da sua assinatura. Renove com antecedência para não perder o acesso às suas ferramentas.',
                    readOnlyTitle: 'Você está no Modo Somente Leitura',
                    readOnlyMessage: 'Seu período de acesso terminou. Você pode consultar seus históricos e relatórios anteriores, mas novos lançamentos e edições estão bloqueados. Assine agora para desbloquear o acesso total.'
                };
                onUpdate(defaults);
            }
        }, (error) => {
            console.warn('DB_SERVICE: Falha ao assinar subscription_alerts:', error);
            const cached = storage.getItemSync('subscriptionAlertSettings', null);
            if (cached) onUpdate(cached);
        });
    } catch (error) {
        console.error('DB_SERVICE: Erro ao assinar subscription_alerts:', error);
        return () => { };
    }
};

/**
 * Marca todas as mensagens do usuário como lidas.
 */
export const subscribePlans = (onUpdate) => {
    try {
        const plansRef = collection(db, 'subscription_plans');
        // Serve cache imediato se disponível
        const cached = storage.getItemSync('cachedSubscriptionPlans', null);
        if (cached) {
            try { onUpdate(JSON.parse(cached)); } catch (_) { }
        }
        return onSnapshot(plansRef, (snapshot) => {
            const plans = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            plans.sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
            storage.setItem('cachedSubscriptionPlans', JSON.stringify(plans));
            onUpdate(plans);
        }, (error) => {
            console.warn('DB_SERVICE: Falha ao assinar subscription_plans:', error);
            const fallback = storage.getItemSync('cachedSubscriptionPlans', null);
            if (fallback) {
                try { onUpdate(JSON.parse(fallback)); } catch (_) { }
            }
        });
    } catch (error) {
        console.error('DB_SERVICE: Erro ao assinar subscription_plans:', error);
        return () => { };
    }
};

/**
 * Marca todas as mensagens do usuário como lidas.
 */
export const markMessagesAsRead = async (userId) => {
    if (!userId) return;
    try {
        const { collection, query, where, getDocs, writeBatch, doc } = await import('firebase/firestore');
        const q = query(
            collection(db, 'support_messages'),
            where('userId', '==', userId),
            where('userRead', '==', false)
        );

        const snapshot = await getDocs(q);
        if (snapshot.empty) return;

        const batch = writeBatch(db);
        snapshot.docs.forEach(d => {
            batch.update(doc(db, 'support_messages', d.id), { userRead: true });
        });

        await batch.commit();
        console.log('DB_SERVICE: Mensagens marcadas como lidas.');
    } catch (error) {
        console.error('DB_SERVICE: Erro ao marcar mensagens como lidas:', error);
    }
};

/**
 * Limpa o bloqueio de tempo (cooldown) de um usuÃ¡rio.
 */
export const resetSupportCooldown = (userId) => {
    if (!userId) return;
    try {
        const LAST_SEND_KEY = `last_support_send_${userId}`;
        storage.removeItem(LAST_SEND_KEY);
        console.log(`DB_SERVICE: Cooldown resetado para o usuÃ¡rio ${userId}`);
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao resetar cooldown:', error);
        return false;
    }
};

/**
 * Verifica se o usuÃ¡rio tem um ticket aberto.
 */
export const hasOpenTicket = async (userId) => {
    if (!userId) return false;
    try {
        const { collection, query, where, getDocs, limit } = await import('firebase/firestore');
        const q = query(
            collection(db, 'support_messages'),
            where('userId', '==', userId),
            where('status', 'in', ['new', 'pending']),
            limit(1)
        );
        const snapshot = await getDocs(q);
        return !snapshot.empty;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao verificar ticket aberto:', error);
        return false;
    }
};

/**
 * Percorre os cupons aplicados do usuÃ¡rio e marca como expirados no Firestore
 * aqueles que jÃ¡ passaram da validade, permitindo que o sistema identifique 
 * o slot de cupom ativo como livre para novas promoÃ§Ãµes (Admin).
 */
export const cleanupExpiredCoupons = async (userId, appliedCoupons) => {
    if (!userId || !appliedCoupons || appliedCoupons.length === 0) return;

    const now = new Date();
    let hasChanges = false;

    const cleanedCoupons = appliedCoupons.map(c => {
        const expiresAt = c.expiresAt ? new Date(c.expiresAt) : null;
        // Se o cupom tem data de expiraÃ§Ã£o e ela jÃ¡ passou, e ainda nÃ£o estÃ¡ marcado como expirado
        if (!c.isExpired && expiresAt && expiresAt <= now) {
            hasChanges = true;
            return { ...c, isExpired: true };
        }
        return c;
    });

    if (hasChanges) {
        try {
            console.log(`DB_SERVICE: Atualizando status de expiraÃ§Ã£o para cupons de ${userId}...`);
            await updateUserDataField(userId, 'appliedCoupons', cleanedCoupons);
            return true;
        } catch (error) {
            console.error('DB_SERVICE: Erro ao limpar cupons expirados:', error);
        }
    }
    return false;
};

/**
 * Atualiza o documento de feature flags (settings/features).
 * Exclusivo para administradores.
 */
export const updateFeatureFlags = async (flags) => {
    try {
        const flagsRef = doc(db, 'settings', 'features');
        await setDoc(flagsRef, flags, { merge: true });
        console.log('DB_SERVICE: Feature flags atualizadas com sucesso.');
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao atualizar feature flags:', error);
        return false;
    }
};

/**
 * Ouve o documento de configuraÃ§Ãµes dos banners de parceiros (settings/partner_banners).
 */
export const subscribePartnerBanners = (onUpdate) => {
    try {
        const bannersRef = doc(db, 'settings', 'partner_banners');
        return onSnapshot(bannersRef, (docSnap) => {
            if (docSnap.exists()) {
                onUpdate(docSnap.data().banners || []);
            } else {
                onUpdate([]);
            }
        });
    } catch (error) {
        console.error('DB_SERVICE: Erro ao assinar partner banners:', error);
        return () => { };
    }
};

/**
 * Atualiza o documento de banners de parceiros (settings/partner_banners).
 * Exclusivo para administradores.
 */
export const updatePartnerBanners = async (banners) => {
    try {
        const bannersRef = doc(db, 'settings', 'partner_banners');
        await setDoc(bannersRef, { banners }, { merge: true });
        console.log('DB_SERVICE: Banners de parceiros atualizados com sucesso.');
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao atualizar banners de parceiros:', error);
        return false;
    }
};

/**
 * Registra um clique em um banner de parceiro.
 * Incrementa contadores de total, dia, semana e mês.
 */
export const registerBannerClick = async (bannerId) => {
    if (!bannerId) return;
    try {
        const today = getTodayDateKey();
        const startOfWeek = getStartOfWeek(today);
        const month = today.substring(0, 7); // YYYY-MM

        const statsRef = doc(db, 'banner_stats', bannerId);

        await runTransaction(db, async (transaction) => {
            const statsDoc = await transaction.get(statsRef);

            if (!statsDoc.exists()) {
                transaction.set(statsRef, {
                    total: 1,
                    day: { date: today, count: 1 },
                    week: { id: startOfWeek, count: 1 },
                    month: { id: month, count: 1 },
                    updatedAt: serverTimestamp()
                });
            } else {
                const data = statsDoc.data();
                const updates = {
                    total: increment(1),
                    updatedAt: serverTimestamp()
                };

                // Lógica de Reset Diário
                if (data.day?.date === today) {
                    updates['day.count'] = increment(1);
                } else {
                    updates.day = { date: today, count: 1 };
                }

                // Lógica de Reset Semanal
                if (data.week?.id === startOfWeek) {
                    updates['week.count'] = increment(1);
                } else {
                    updates.week = { id: startOfWeek, count: 1 };
                }

                // Lógica de Reset Mensal
                if (data.month?.id === month) {
                    updates['month.count'] = increment(1);
                } else {
                    updates.month = { id: month, count: 1 };
                }

                transaction.update(statsRef, updates);
            }
        });

        console.log(`DB_SERVICE: Clique registrado para banner ${bannerId}`);
    } catch (error) {
        console.error('DB_SERVICE: Erro ao registrar clique no banner:', error);
    }
};

/**
 * Salva o catálogo de produtos da Loja a Bordo no Firestore.
 * Usa merge no documento principal do usuário para economizar leituras.
 */
export const saveLojaItems = async (userId, items) => {
    if (!userId) return false;
    try {
        const userDocRef = doc(db, COLLECTION_NAME, String(userId), 'private', DOCUMENT_ID);
        await setDoc(userDocRef, {
            lojaABordoItems: sanitizePayload(items || []),
            lastSynced: serverTimestamp()
        }, { merge: true });
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao salvar itens da Loja a Bordo:', error);
        return false;
    }
};

/**
 * Busca o catálogo de produtos da Loja a Bordo do Firestore.
 */
export const fetchLojaItems = async (userId) => {
    if (!userId) return [];
    try {
        const userDocRef = doc(db, COLLECTION_NAME, String(userId), 'private', DOCUMENT_ID);
        const snap = await getDoc(userDocRef);
        if (snap.exists()) {
            return snap.data().lojaABordoItems || [];
        }
        return [];
    } catch (error) {
        console.error('DB_SERVICE: Erro ao buscar itens da Loja a Bordo:', error);
        return [];
    }
};

/**
 * Salva as categorias personalizadas da Loja a Bordo no Firestore.
 */
export const saveLojaCategories = async (userId, categories) => {
    if (!userId) return false;
    try {
        const userDocRef = doc(db, COLLECTION_NAME, String(userId), 'private', DOCUMENT_ID);
        await setDoc(userDocRef, {
            lojaABordoCategories: sanitizePayload(categories || []),
            lastSynced: serverTimestamp()
        }, { merge: true });
        return true;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao salvar categorias da Loja a Bordo:', error);
        return false;
    }
};

/**
 * Busca as categorias personalizadas da Loja a Bordo do Firestore.
 */
export const fetchLojaCategories = async (userId) => {
    if (!userId) return null;
    try {
        const userDocRef = doc(db, COLLECTION_NAME, String(userId), 'private', DOCUMENT_ID);
        const snap = await getDoc(userDocRef);
        if (snap.exists() && snap.data().lojaABordoCategories) {
            return snap.data().lojaABordoCategories;
        }
        return null;
    } catch (error) {
        console.error('DB_SERVICE: Erro ao buscar categorias da Loja a Bordo:', error);
        return null;
    }
};
